import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { X, CheckCircle, AlertTriangle, XCircle, Info, Trash2 } from 'lucide-react';

// ==================== TOAST ====================

const ToastContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within AppDialogProvider');
  return ctx;
}

function ToastItem({ toast, onDismiss }) {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (toast.duration === 0) return;
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(() => onDismiss(toast.id), 300);
    }, toast.duration || 4000);
    return () => clearTimeout(timer);
  }, [toast, onDismiss]);

  const dismiss = () => { setExiting(true); setTimeout(() => onDismiss(toast.id), 300); };

  const icons = {
    success: <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />,
    error: <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />,
    warning: <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />,
    info: <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />,
  };

  const borders = {
    success: 'border-l-emerald-500',
    error: 'border-l-red-500',
    warning: 'border-l-amber-500',
    info: 'border-l-blue-500',
  };

  return (
    <div
      className={`flex items-start gap-3 bg-white rounded-lg shadow-lg border border-gray-200 border-l-4 ${borders[toast.type] || borders.info} p-4 max-w-sm w-full transition-all duration-300 ${exiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}
      style={{ animation: exiting ? 'none' : 'slideIn 0.3s ease-out' }}
    >
      {icons[toast.type] || icons.info}
      <div className="flex-1 min-w-0">
        {toast.title && <p className="text-sm font-semibold text-gray-800">{toast.title}</p>}
        <p className="text-sm text-gray-600">{toast.message}</p>
      </div>
      <button onClick={dismiss} className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 rounded transition-colors">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ==================== CONFIRM DIALOG ====================

const ConfirmContext = createContext(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within AppDialogProvider');
  return ctx;
}

function ConfirmDialog({ dialog, onResolve }) {
  if (!dialog) return null;

  const { title, message, confirmText, cancelText, type } = dialog;

  const typeStyles = {
    danger: { icon: <Trash2 className="w-6 h-6 text-red-500" />, bg: 'bg-red-50', confirmBtn: 'bg-red-600 hover:bg-red-700' },
    warning: { icon: <AlertTriangle className="w-6 h-6 text-amber-500" />, bg: 'bg-amber-50', confirmBtn: 'bg-amber-600 hover:bg-amber-700' },
    info: { icon: <Info className="w-6 h-6 text-blue-500" />, bg: 'bg-blue-50', confirmBtn: 'bg-blue-600 hover:bg-blue-700' },
  };
  const style = typeStyles[type] || typeStyles.info;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ animation: 'fadeIn 0.15s ease-out' }}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => onResolve(false)} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" style={{ animation: 'scaleIn 0.2s ease-out' }}>
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl ${style.bg} flex items-center justify-center flex-shrink-0`}>
              {style.icon}
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-600 mt-1">{message}</p>
            </div>
          </div>
        </div>
        <div className="flex gap-3 px-6 pb-6 justify-end">
          <button
            onClick={() => onResolve(false)}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {cancelText || 'Abbrechen'}
          </button>
          <button
            onClick={() => onResolve(true)}
            className={`px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-colors ${style.confirmBtn}`}
          >
            {confirmText || 'Bestätigen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== PROVIDER ====================

export function AppDialogProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const resolveRef = useRef(null);
  const toastIdRef = useRef(0);

  const toast = useCallback((message, type = 'info', opts = {}) => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev.slice(-4), { id, message, type, title: opts.title, duration: opts.duration ?? 4000 }]);
    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toastApi = {
    success: (msg, opts) => toast(msg, 'success', opts),
    error: (msg, opts) => toast(msg, 'error', { duration: 6000, ...opts }),
    warning: (msg, opts) => toast(msg, 'warning', opts),
    info: (msg, opts) => toast(msg, 'info', opts),
  };

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setConfirmDialog({
        title: opts.title || 'Bestätigung',
        message: opts.message || 'Möchten Sie fortfahren?',
        confirmText: opts.confirmText,
        cancelText: opts.cancelText,
        type: opts.type || 'info',
      });
    });
  }, []);

  const handleConfirmResolve = (result) => {
    if (resolveRef.current) resolveRef.current(result);
    resolveRef.current = null;
    setConfirmDialog(null);
  };

  return (
    <ToastContext.Provider value={toastApi}>
      <ConfirmContext.Provider value={confirm}>
        {children}
        {/* Toast Container */}
        <div className="fixed top-4 right-4 z-[110] flex flex-col gap-2 pointer-events-none">
          {toasts.map(t => (
            <div key={t.id} className="pointer-events-auto">
              <ToastItem toast={t} onDismiss={dismissToast} />
            </div>
          ))}
        </div>
        {/* Confirm Dialog */}
        <ConfirmDialog dialog={confirmDialog} onResolve={handleConfirmResolve} />
        {/* Animations */}
        <style>{`
          @keyframes slideIn { from { opacity: 0; transform: translateX(1rem); } to { opacity: 1; transform: translateX(0); } }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        `}</style>
      </ConfirmContext.Provider>
    </ToastContext.Provider>
  );
}
